// JARVIS — Indian Market API Client
// Calls the Express proxy (port 3001) → yahoo-finance2 → NSE/BSE data

const BASE = '/api/india';

async function apiFetch(path) {
  const res  = await fetch(`${BASE}${path}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// Live Nifty 50, Bank Nifty, Sensex + sector indices
export async function getIndices() {
  return apiFetch('/indices');
}

// Full quote + fundamentals for one stock
// symbol: 'RELIANCE.NS', 'TCS.NS', etc.
export async function getQuote(symbol) {
  return apiFetch(`/quote?symbol=${encodeURIComponent(symbol)}`);
}

// OHLCV history — period: '1mo' | '3mo' | '6mo' | '1y' | '2y'
export async function getHistory(symbol, period = '3mo') {
  return apiFetch(`/history?symbol=${encodeURIComponent(symbol)}&period=${period}`);
}

// Search Nifty 100 by name/symbol (local, instant)
export async function searchStocks(query) {
  return apiFetch(`/search?q=${encodeURIComponent(query)}`);
}

// All 100 stocks with live quotes (first call ~10-15s, cached after)
export async function getNifty100() {
  return apiFetch('/nifty100');
}

// F&O contract info: index contracts + all F&O stocks with lot sizes
export async function getFnOInfo() {
  return apiFetch('/fno');
}

// Health check
export async function getIndiaHealth() {
  return apiFetch('/health');
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function formatPrice(price) {
  if (price == null) return '—';
  return '₹' + price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatMarketCap(cap) {
  if (!cap) return '—';
  if (cap >= 1e12) return '₹' + (cap / 1e12).toFixed(2) + 'T';
  if (cap >= 1e9)  return '₹' + (cap / 1e9).toFixed(2)  + 'B';
  if (cap >= 1e7)  return '₹' + (cap / 1e7).toFixed(2)  + 'Cr';
  return '₹' + cap.toFixed(0);
}

export function formatVolume(vol) {
  if (!vol) return '—';
  if (vol >= 1e7) return (vol / 1e7).toFixed(2) + 'Cr';
  if (vol >= 1e5) return (vol / 1e5).toFixed(2) + 'L';
  return vol.toLocaleString('en-IN');
}

export function changeColor(changePct) {
  if (changePct == null) return '#9ca3af';
  return changePct >= 0 ? '#10b981' : '#ef4444';
}

export function changeSign(changePct) {
  if (changePct == null) return '';
  return changePct >= 0 ? '+' : '';
}

// Is NSE market open right now? (9:15 - 15:30 IST, Mon-Fri)
export function isMarketOpen() {
  const now = new Date();
  const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const day  = ist.getDay(); // 0=Sun, 6=Sat
  const mins = ist.getHours() * 60 + ist.getMinutes();
  if (day === 0 || day === 6) return false;
  return mins >= 9 * 60 + 15 && mins <= 15 * 60 + 30;
}

// Market session label
export function getMarketSession() {
  const now = new Date();
  const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const day  = ist.getDay();
  const mins = ist.getHours() * 60 + ist.getMinutes();
  if (day === 0 || day === 6) return { label: 'WEEKEND', color: '#6b7280', open: false };
  if (mins < 9 * 60)                          return { label: 'PRE-MARKET', color: '#f59e0b', open: false };
  if (mins < 9 * 60 + 15)                     return { label: 'OPENING', color: '#f97316', open: false };
  if (mins <= 15 * 60 + 30)                   return { label: 'MARKET OPEN', color: '#10b981', open: true };
  if (mins <= 16 * 60)                         return { label: 'AFTER HOURS', color: '#6b7280', open: false };
  return { label: 'CLOSED', color: '#6b7280', open: false };
}
